import type { Identifier } from "ra-core";

import type { DealActivityResult, DealActivityType } from "../types";
import type { MessagePlaceholderValues } from "../activityLog/activityLogRules";

/**
 * Almikia Epic EA (TASK-113): pure, framework-free helpers backing the
 * direccion-only "Actividades" catalogue admin screen -- name/message
 * template/attempt cap validation, next-position computation, list ordering
 * and error-message mapping. Kept free of react-admin/react coupling so they
 * are unit-testable on their own (see `activityTypeForm.test.ts`), mirroring
 * the shape of `stageConfigs/stageConfigForm.ts`. The exact limits and
 * messages mirror `activityCatalogueProvider.ts`'s FakeRest guards (DATA
 * CONTRACT EA (1)), which mirror the SQL checks.
 */

export const ACTIVITY_NAME_MAX_LENGTH = 80;
export const MESSAGE_TEMPLATE_MAX_LENGTH = 1000;
export const MIN_ATTEMPT_CAP = 1;
export const MAX_ATTEMPT_CAP = 10;

/**
 * DATA CONTRACT EA (1): an activity `name`, trimmed non-blank, at most 80
 * chars, unique case-insensitively among `activeNames` (every OTHER
 * non-archived activity's name -- the caller excludes the record's own
 * current name before calling this on an edit).
 */
export function validateActivityName(
  name: string | null | undefined,
  activeNames: readonly string[],
): string | undefined {
  const trimmed = (name ?? "").trim();
  if (trimmed === "") {
    return "resources.deal_activity_types.error.name_required";
  }
  if (trimmed.length > ACTIVITY_NAME_MAX_LENGTH) {
    return "resources.deal_activity_types.error.name_too_long";
  }
  const normalized = trimmed.toLowerCase();
  const duplicate = activeNames.some(
    (existing) => existing.trim().toLowerCase() === normalized,
  );
  if (duplicate) {
    return "resources.deal_activity_types.error.name_duplicate";
  }
  return undefined;
}

/** DATA CONTRACT EA (1): `message_template`, optional, at most 1000 chars. */
export function validateMessageTemplate(
  value: string | null | undefined,
): string | undefined {
  if (value == null || value === "") return undefined;
  if (value.length > MESSAGE_TEMPLATE_MAX_LENGTH) {
    return "resources.deal_activity_types.error.message_template_too_long";
  }
  return undefined;
}

/** DATA CONTRACT EA (1): `max_attempts`, optional, integer between 1 and 10. */
export function validateMaxAttempts(
  value: number | string | null | undefined,
): string | undefined {
  if (value == null || value === "") return undefined;
  const num = typeof value === "string" ? Number(value) : value;
  if (
    !Number.isInteger(num) ||
    num < MIN_ATTEMPT_CAP ||
    num > MAX_ATTEMPT_CAP
  ) {
    return "resources.deal_activity_types.error.max_attempts_invalid";
  }
  return undefined;
}

/**
 * A new activity is added at the end of the order (ticket description):
 * one position step past the highest `position` already used (archived rows
 * included, so a freed slot is never reused) -- mirrors
 * `stageConfigForm.ts`'s `nextStagePosition`.
 */
export function nextActivityTypePosition(
  activityTypes: readonly { position: number }[],
): number {
  const positions = activityTypes.map((activity) => activity.position);
  return positions.length > 0 ? Math.max(...positions) + 10 : 10;
}

/** Ascending `(position, id)` -- the shared tie-break used across EA screens. */
function comparePositionThenId(
  a: { position: number; id: Identifier },
  b: { position: number; id: Identifier },
): number {
  if (a.position !== b.position) return a.position - b.position;
  return String(a.id).localeCompare(String(b.id), undefined, {
    numeric: true,
  });
}

/**
 * The catalogue list's row order (ticket acceptance criterion): active
 * (non-archived) activities first, then archived ones, each group by
 * `(position, id)`.
 */
export function orderActivityTypesForList(
  activityTypes: readonly DealActivityType[],
): DealActivityType[] {
  const active = activityTypes
    .filter((activity) => activity.archived_at == null)
    .slice()
    .sort(comparePositionThenId);
  const archived = activityTypes
    .filter((activity) => activity.archived_at != null)
    .slice()
    .sort(comparePositionThenId);
  return [...active, ...archived];
}

/** The number of non-archived results belonging to `activityTypeId`. */
export function countActiveResults(
  activityTypeId: Identifier,
  results: readonly DealActivityResult[],
): number {
  return results.filter(
    (result) =>
      result.activity_type_id === activityTypeId && result.archived_at == null,
  ).length;
}

/**
 * Sample values for the create/edit form's live message-preview -- rendered
 * through the shared TASK-110 `renderMessageTemplate` helper, never
 * persisted (the ticket's own acceptance criterion: a live preview, the
 * stored template is untouched).
 */
export const SAMPLE_MESSAGE_PLACEHOLDER_VALUES: MessagePlaceholderValues = {
  nombre: "Ana",
  asesora: "Renata",
  proyecto: "Nombre del proyecto",
};

/**
 * DATA CONTRACT EA (1): maps a save/write error to a Spanish message, so a
 * raw Postgres/RLS error text is never shown to direccion -- mirrors
 * `stageConfigForm.ts`'s `toStageConfigErrorMessage`. A FakeRest guard error
 * is already a plain Spanish message (see `activityCatalogueProvider.ts`),
 * so it passes through UNCHANGED here -- only a message-less error falls
 * back to the generic message.
 */
export function toActivityTypeErrorMessage(error: unknown): string {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code?: unknown }).code)
      : undefined;
  const message = error instanceof Error ? error.message : undefined;

  if (code === "23505" || /duplicate key/i.test(message ?? "")) {
    return "resources.deal_activity_types.error.name_duplicate";
  }
  if (code === "42501" || /row-level security/i.test(message ?? "")) {
    return "resources.deal_activity_types.error.not_authorized";
  }
  return message || "resources.deal_activity_types.error.generic";
}
